import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Grid,
  Card,
  CardMedia,
  CardContent,
  CardActions,
  CircularProgress,
  Alert,
  Snackbar,
  Pagination,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import DeleteIcon from '@mui/icons-material/Delete';
import { BASE_URL } from '../../utils/endpoints';
import ViewCategoryDealCreate from './ViewCategoryDealCreate';

const ViewCategoryDealList = () => {
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'info', 
  });

  
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(6); 

  
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [bannerToDeleteId, setBannerToDeleteId] = useState(null);

  useEffect(() => {
    const fetchBanners = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${BASE_URL}/clientbanners`);
        if (!response.ok) {
          throw new Error('Network response was not ok');
        }
        const data = await response.json();
        setBanners(data.reverse());
      } catch (err) {
        console.error('Error fetching banners:', err);
        setError('Failed to fetch banners. Please try again.');
        setSnackbar({
          open: true,
          message: 'Failed to fetch banners. Please try again.',
          severity: 'error',
        });
      } finally {
        setLoading(false);
      }
    };
    fetchBanners();
  }, []);

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentBanners = banners.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(banners.length / itemsPerPage);

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
  };

  const handleDeleteClick = (id) => {
    setBannerToDeleteId(id);
    setOpenConfirmDialog(true);
  };

  const handleCloseConfirmDialog = () => {
    setOpenConfirmDialog(false);
    setBannerToDeleteId(null);
  };

  const confirmDelete = async () => {
    handleCloseConfirmDialog(); 
    try {
      setBanners(banners.filter((banner) => banner._id !== bannerToDeleteId));
      const response = await fetch(`${BASE_URL}/clientbanner/${bannerToDeleteId}`, {
        method: 'DELETE',
      });
      if (!response.ok) {
        throw new Error('Failed to delete banner');
      }
      setSnackbar({
        open: true,
        message: 'Banner deleted successfully!',
        severity: 'success',
      });
      if (currentBanners.length === 1 && currentPage > 1 && totalPages > 1) {
        setCurrentPage(currentPage - 1);
      }
    } catch (err) {
      console.error('Error deleting banner:', err);
      setError('Failed to delete banner. Please try again.');
      setSnackbar({
        open: true,
        message: 'Failed to delete banner. Please try again.',
        severity: 'error',
      });
      
      const fetchBanners = async () => {
        try {
          const response = await fetch(`${BASE_URL}/clientbanners`);
          if (!response.ok) {
            throw new Error('Failed to refetch banners');
          }
          const data = await response.json();
          setBanners(data);
        } catch (err) {
          setError('Failed to refetch banners after delete attempt.');
          setSnackbar({
            open: true,
            message: 'Failed to refetch banners after delete attempt.',
            severity: 'error',
          });
        }
      };
      fetchBanners();
    }
  };

  const handleDealCreated = (newDeal) => {
    setBanners([...banners, newDeal]);
    setSnackbar({
      open: true,
      message: 'Banner created successfully!',
      severity: 'success',
    });
    setIsModalOpen(false); 
    
    
    if (banners.length === 0) {
      setCurrentPage(1);
    } else {
      const newTotalPages = Math.ceil((banners.length + 1) / itemsPerPage);
      if (newTotalPages > totalPages) {
        setCurrentPage(newTotalPages);
      }
    }
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  
  const cardVariants = {
    hidden: { opacity: 0, y: 50, scale: 0.8 },
    visible: { opacity: 1, y: 0, scale: 1, transition: { duration: 0.5, ease: 'easeOut' } },
    exit: { opacity: 0, y: -50, scale: 0.8, transition: { duration: 0.3, ease: 'easeIn' } },
  };

  return (
    <Box sx={{ p: 0, mt: 2, bgcolor: '#fff', borderRadius: 2 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 4,
          p: 2,
          bgcolor: '#fff',
          borderRadius: 2,
        }}
      >
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.0 }}
        >
          <Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: '#2e7d32', mb: 1 }}>
            Category Deals
          </Typography>
          <Typography variant="body2" color="#2e7d32">
            Dashboard • Category Deals • List
          </Typography>
        </motion.div>
        <Button
          variant="contained"
          color="primary"
          onClick={() => setIsModalOpen(true)}
          sx={{
            backgroundColor: '#2e7d32',
            '&:hover': {
              backgroundColor: '#388e3c',
            },
          }}
        >
          Add New Deal
        </Button>
      </Box>

      <ViewCategoryDealCreate
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onDealCreated={handleDealCreated}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      <Dialog
        open={openConfirmDialog}
        onClose={handleCloseConfirmDialog}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">{"Confirm Deletion"}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to delete this banner? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseConfirmDialog} color="primary">
            Cancel
          </Button>
          <Button onClick={confirmDelete} color="error" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mt: 8 }}>
          <CircularProgress />
          <Typography variant="h6" sx={{ ml: 2, color: 'text.secondary' }}>
            Loading Banners...
          </Typography>
        </Box>
      ) : error ? (
        <Alert severity="error" sx={{ mt: 4, width: '100%' }}>
          {error}
        </Alert>
      ) : banners.length === 0 ? (
        <Box sx={{ mt: 4, textAlign: 'center', p: 4, bgcolor: '#fff', borderRadius: 2, boxShadow: 1 }}>
          <Typography variant="h6" color="text.secondary">
            No category deals found. Click "Add New Deal" to get started!
          </Typography>
        </Box>
      ) : (
        <>
          <Grid
            container
            spacing={4}
            sx={{
              display: 'flex',
              justifyContent: 'center',
            }}
          >
            <AnimatePresence>
              {currentBanners.map((banner) => (
                <Grid
                  item
                  key={banner._id}
                  xs={12}
                  sm={6}
                  md={4}
                  lg={3}
                  component={motion.div}
                  variants={cardVariants}
                  initial="hidden"
                  animate="visible"
                  exit="exit"
                  layout
                >
                  <Card
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      height: '100%',
                      borderRadius: 2,
                      boxShadow: 3,
                      transition: 'transform 0.2s ease-in-out',
                      '&:hover': {
                        transform: 'translateY(-5px)',
                        boxShadow: 6,
                      },
                    }}
                  >
                    <a
                      href={banner.visit_link || '#'}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        textDecoration: 'none',
                        cursor: banner.visit_link ? 'pointer' : 'default',
                        display: 'block',
                      }}
                    >
                      <CardMedia
                        component="img"
                        image={banner.image_url || 'https://via.placeholder.com/200?text=No+Image'}
                        alt={banner.brand_name || 'No name'}
                        sx={{
                          objectFit: 'cover',
                          transition: 'opacity 0.2s ease-in-out',
                          '&:hover': {
                            opacity: banner.visit_link ? 0.8 : 1,
                          },
                          cursor: 'pointer',
                        }}
                      />
                    </a>
                    <CardContent sx={{ flexGrow: 1, p: 2 }}>
                      <Typography gutterBottom variant="h6" component="div" sx={{ fontWeight: 600 }}>
                        {banner.brand_name || 'No name'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Category: {banner.category || 'N/A'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Status:{' '}
                        <span style={{ color: banner.status === 'approved' ? '#2e7d32' : 'orange' }}>
                          {banner.status}
                        </span>
                      </Typography>
                    </CardContent>
                    <CardActions sx={{ p: 2, pt: 0, justifyContent: 'flex-end' }}>
                      <Button
                        size="small"
                        variant="contained"
                        color="error"
                        startIcon={<DeleteIcon />}
                        onClick={() => handleDeleteClick(banner._id)}
                      >
                        Delete
                      </Button>
                    </CardActions>
                  </Card>
                </Grid>
              ))}
            </AnimatePresence>
          </Grid>
          {/* Pagination Controls */}
          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4, mb: 2 }}>
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={handlePageChange}
                color="primary"
                size="large"
              />
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default ViewCategoryDealList;